<template>
<Loader v-if="!isLoaded" data-component="PromotionPage.vue"/>
<div v-else data-component="PromotionPage.vue">
	<div
		:class="{'flex flex-col-reverse': ScreenSize <= 1}"
	>
		<MessageBar
			v-if="showConfirmationMessageBar"
			:message-title="`Promowanie jest włączone na ${enabledListsLength} ${enabledListsLength === 1 ? 'liście' : 'listach'}.`"
			message-content="Usługa zostanie przedłużona w zależności od wybranej kategorii. Punkty zostaną pobrane automatycznie."
			message-type="info"
			class="mb-8"
			:class="{'mt-10': ScreenSize > 1}"
			@message-button-action="closeMessageBar"
		/>

		<PageHeader
			:Title="pageHeader"
			:Subtitle="isAdminModule ? String(customerId) : ''"
			:Back="!isAdminModule || isSummaryPage"
			:BackOnDesktop="isSummaryPage"
			:EmitInstead="true"
			:class="{ 'pb-5': isAdminModule }"
			@back-click="goBack"
		/>
	</div>

	<template v-if="isSummaryPage">
		<PromotionSummary/>
	</template>
	<template v-else>
		<PromotionIntroduction
			v-if="!isAdminModule"
		/>

		<div v-if="!isAdminModule" class="text-right">
			<hr class="promotion-hr h-0.5 bg-neutral-100 ml-auto">
		</div>

		<PromotionActivePromotions
			ref="activePromotions"
			:apiNamespace="apiNamespace"
			:enabledPromotions="enabledPromotions"
			:isPromotionsLimitReached="isPromotionsLimitReached"
			class="py-5"
			@assign-enabled-promotions-current-sort="assignEnabledPromotionsCurrentSort"
			@assign-enabled-promotions-current-sort-dir="assignEnabledPromotionsCurrentSortDir"
			@get-enabled-promotions="getEnabledPromotions"
		/>

		<div v-if="isMobile && enabledPromotions.length" class="text-right">
			<hr class="promotion-hr h-0.5 bg-neutral-100 ml-auto">
		</div>

		<PromotionSearch
			ref="promotionSearch"
			:apiNamespace="apiNamespace"
			:enabledPromotions="enabledPromotions"
			:isPromotionsLimitReached="isPromotionsLimitReached"
			@get-all-locations-per-category="getAllLocationsPerCategory"
			@toggle-map-modal="toggleMapModal"
		/>

		<PromotionCart
			:apiNamespace="apiNamespace"
			:isTutorialOpen="isTutorialOpen"
			:isPromotionsLimitReached="isPromotionsLimitReached"
			@toggle-map-modal="toggleMapModal"
		/>

		<ModalDialog
			class="absolute flex flex-col items-center justify-center p-3 text-center bg-white promotion-limit-modal-wrapper is-dialog"
			style="z-index: 2049"
			:Visible="isLimitModalVisible"
		>
			<button
				class="absolute top-0 right-0 p-3 z-32"
				@click="togglePromotionLimitModal(false)"
			>
				<BaseIcon name="close" size="1"/>
			</button>
			<div>
				<BaseIcon name="exclamation-circle" size="4" class="mb-3" style="color: var(--accent);"/>
				<div class="mb-2 text-black heading-large">
					Nie możesz wybrać więcej niż 20 list
				</div>
				<div class="mx-auto mt-7" style="width: 100%; max-width: 320px;">
					<button class="w-full btn btn-primary" @click="togglePromotionLimitModal(false)">
						<div>
							Rozumiem
						</div>
					</button>
				</div>
			</div>
		</ModalDialog>

		<PromotionMap
			v-if="isMapModalVisible"
			:preselectedLocations="mapPreselectedLocations"
			:preselectedLocationsFromCart="mapPreselectedLocationsFromCart"
			:blockedLocations="blockedLocations"
			:isPromotionsLimitReached="isPromotionsLimitReached"
			:namesAndIdsLocations="mapNamesAndIdsLocations"
			@toggle-map-modal="toggleMapModal"
			@assign-selected-locations="assignSelectedLocations"
			@update-cart-from-map="updateCartFromMap"
		/>

		<PromotionDisableRenewModal
			v-if="isDisableRenewModalVisible"
		/>

		<PromotionFeedbackModal
			v-if="isFeedbackModalVisible"
			:apiNamespace="apiNamespace"
		/>

		<!-- SOON -->
		<PromotionTutorial v-if="showOnboarding && false" @is-tutorial-visible="isTutorialVisible"/>
	</template>

	<PromotionFillProfileModal
		v-if="isFillProfileModalVisible"
	/>

	<!-- SOON -->
	<PromotionTutorial v-if="showOnboarding && false" @is-tutorial-visible="isTutorialVisible"/>

	<PromotionSummaryModals v-if="isPromotionModalVisible || Boolean($route.query.rechargeAccount)"/>
</div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue';
import { mapState, mapWritableState, mapActions } from 'pinia';
import { usePromotionStore } from '../../../stores/promotion';
import { useAppStore } from '../../../stores/app';
import AllLocationsPerCategory from '../../../types/interfaces/promotion/AllLocationsPerCategory';
import GeoTreeLocationItem from '../../../types/interfaces/map/GeoTreeLocationItem';
import PromotionIntroduction from './PromotionIntroduction.vue';
import PromotionSearch from './PromotionSearch.vue';
import PromotionActivePromotions from './PromotionActivePromotions.vue';
import serverFetch from '../../../globals/ServerFetch';

// @vuese
// Komponent odpowiedzialny za ekran usługi Promowania.
// DOKUMENTACJA PROMOWANIA --- https://oferteo.atlassian.net/wiki/spaces/DEV/pages/293339137/FRONT+Promowanie+2.0 ---
// @group Pages | Profile
export default {
	name: 'PromotionProfile',
	components: {
		PromotionIntroduction,
		PromotionSearch,
		PromotionActivePromotions,
		PromotionCart: defineAsyncComponent(() => import('./PromotionCart.vue')),
		PromotionSummary: defineAsyncComponent(() => import('./PromotionSummary.vue')),
		PromotionTutorial: defineAsyncComponent(() => import('./PromotionTutorial.vue')),
		PromotionMap: defineAsyncComponent(() => import('./PromotionMap.vue')),
		PromotionDisableRenewModal: defineAsyncComponent(() => import('./modals/PromotionDisableRenewModal.vue')),
		PromotionFeedbackModal: defineAsyncComponent(() => import('./modals/PromotionFeedbackModal.vue')),
		PromotionFillProfileModal: defineAsyncComponent(() => import('./modals/PromotionFillProfileModal.vue')),
		MessageBar: defineAsyncComponent(() => import('../../../globals/components/base/MessageBar.vue')),
		PromotionSummaryModals: defineAsyncComponent(() => import('./modals/PromotionSummaryModals.vue')),
	},
	beforeRouteUpdate(to, from, next) {
		if (to.name === 'PromotionProfile') {
			this.isSummaryPage = false;
		}

		next();
	},
	data() {
		return {
			isLoaded: false,
			isChangePromotionModalOpen: false,
			isPromotionEnabled: false,
			isFullContentVisible: false,
			isTutorialOpen: false,
			showOnboarding: false,
			promotionContent: `Promuj swoją firmę i docieraj do tysięcy Klientów bez wysiłku.
		Wskaż, gdzie mamy Cię promować i znajdź się na szczycie wyników wyszukiwania.
		Pozyskuj nowe, darmowe kontakty i nie martw się o utratę wysokiej pozycji dzięki stałej, miesięcznej opłacie.
		<span id="more-about-promotion" class="font-bold cursor-pointer">Więcej</span>`,
			isMapModalVisible: false,
			locationsTree: {},
			selectedLocations: [] as {key: string, item: GeoTreeLocationItem }[],
			allLocationsPerCategory: [] as AllLocationsPerCategory[],
			enabledPromotionsCurrentSort: 'name',
			enabledPromotionsCurrentSortDir: 'asc',
		};
	},
	computed: {
		...mapState(useAppStore, [
			'isAdminModule',
		]),
		...mapState(usePromotionStore, [
			'customerId',
			'enabledPromotions',
			'isFeedbackModalVisible',
			'isDisableRenewModalVisible',
			'isFillProfileModalVisible',
			'mobileScrollTopPosition',
			'enabledListsLength',
			'isPromotionModalVisible',
			'selectedCategoryId',
		]),
		...mapWritableState(usePromotionStore, [
			'cartPromotions',
			'isLimitModalVisible',
			'isSummaryPage',
			'showConfirmationMessageBar',
		]),

		pageHeader() {
			if (this.isSummaryPage) {
				return 'Wybierz pakiet Promowania';
			}

			return this.$route.name === 'PromotionSummary' ? 'Włącz Promowanie' : 'Promuj firmę';
		},
		isMobile() {
			return (this.$root as any).$data.WindowWidth <= 768;
		},
		apiNamespace() {
			return this.isAdminModule ? 'admin' : 'promotion';
		},
		isPromotionsLimitReached() {
			const selectedLocationsCount = this.isMapModalVisible
				? this.selectedLocations.reduce((count, { key }) => (this.cartPromotions.some(({ locationId }) => locationId === key) ? count : count + 1), 0)
				: 0;

			return this.enabledPromotions.length + this.cartPromotions.length + selectedLocationsCount >= 20;
		},
		mapPreselectedLocations() {
			return this.enabledPromotions.reduce((accumulator, { idLocation, idCategory }) => {
				if (Number(this.selectedCategoryId) === idCategory) {
					accumulator.push(idLocation);
				}

				return accumulator;
			}, [] as string[]);
		},
		mapPreselectedLocationsFromCart() {
			return this.cartPromotions.reduce((accumulator, { locationId, categoryId }) => {
				if (locationId && Number(this.selectedCategoryId) === categoryId) {
					accumulator.push(locationId);
				}

				return accumulator;
			}, [] as string[]);
		},
		mapNamesAndIdsLocations() {
			return this.allLocationsPerCategory.map(({ locationId, location }) => ({ locationId, locationName: location }));
		},
		blockedLocations() {
			return this.allLocationsPerCategory.filter(({ available }) => !available).map(({ locationId }) => locationId);
		},
	},
	mounted() {
		if (!this.isAdminModule) {
			this.loadCartFromLocalStorage();
		}

		this.getEnabledPromotions({ scrollEnabledList: false });

		const promotionOnboarding = window.localStorage.getItem('PromotionOnboarding');

		if (!promotionOnboarding || promotionOnboarding !== 'done') {
			this.showOnboarding = true;
		}

		this.isLoaded = true;
	},
	methods: {
		...mapActions(usePromotionStore, [
			'getEnabledPromotions',
			'saveCartInLocalStorage',
			'loadCartFromLocalStorage',
			'addToCart',
			'togglePromotionLimitModal',
			'clearCart',
			'clearInputs',
		]),

		goBack() {
			if (this.$route.name === 'PromotionSummary') {
				this.$router.push({ name: 'PromotionProfile' });

				setTimeout(() => {
					window.scrollTo({
						top: this.mobileScrollTopPosition,
					});
				}, 50);

				return;
			}

			window.location.href = `${window.location.origin}/konto/profil`;
		},
		toggleVisibleContent() {
			this.isFullContentVisible = !this.isFullContentVisible;
		},
		isTutorialVisible(value: boolean) {
			this.isTutorialOpen = value;
		},
		toggleMapModal(value: boolean) {
			if (!this.selectedCategoryId) {
				return;
			}

			if (!value) {
				this.selectedLocations.length = 0;
			}

			this.isMapModalVisible = value;
		},
		assignSelectedLocations(selectedLocations : {key: string, item: GeoTreeLocationItem }[]) {
			this.selectedLocations = [...selectedLocations];
		},
		assignEnabledPromotionsCurrentSort(enabledPromotionsCurrentSort: string) {
			this.enabledPromotionsCurrentSort = enabledPromotionsCurrentSort;
		},
		assignEnabledPromotionsCurrentSortDir(enabledPromotionsCurrentSortDir: string) {
			this.enabledPromotionsCurrentSortDir = enabledPromotionsCurrentSortDir;
		},
		updateCartFromMap() {
			this.cartPromotions = this.cartPromotions.filter(({ categoryId, locationId }) => categoryId !== Number(this.selectedCategoryId) || locationId === 'location');

			this.selectedLocations.forEach((selectedLocation) => {
				const foundLocation = this.allLocationsPerCategory.find(({ locationId }) => locationId === selectedLocation.key);

				if (!foundLocation) {
					return;
				}

				const item = {
					categoryId: Number(this.selectedCategoryId),
					locationId: selectedLocation.key,
					price: foundLocation.price,
					category: foundLocation.category,
					location: foundLocation.location,
					listUrl: foundLocation.listUrl,
					isNew: foundLocation.isNew,
					takenPositions: foundLocation.takenPositions,
					isDisabled: false,
				};

				this.addToCart(item, this.isPromotionsLimitReached);
			});

			this.toggleMapModal(false);

			this.clearInputs();

			(this.$root as any).$refs.Toaster.Show('Zaktualizowano koszyk');
		},
		async getAllLocationsPerCategory(categoryId: string) {
			try {
				const { promotionLocationsData } = await serverFetch(`/promotion/get-all-promotion-locations-data.ofer?locationCategoryDTO.categoryId=${categoryId}`);

				this.allLocationsPerCategory = [...promotionLocationsData];
			} catch (error) {
				console.error(error);
			}
		},
		goToTheAdditionalServicesAdminPage() {
			document.location.replace(`/administracja/firmy/${this.customerId}/uslugi-dodatkowe`);
		},
		closeMessageBar() {
			this.showConfirmationMessageBar = false;
		},
	},
};
</script>
<style lang="scss" scoped>
.promotion-hr {
	width: calc(100% - 1.25rem);
}
.promotion-limit-modal-wrapper :deep(.modal-dialog-wrapper) {
	border-radius: 8px;
	box-shadow: 0px 4px 4px 0px #00000040;
}
.promotion-limit-modal-wrapper :deep(.modal-dialog-content) {
	padding: 60px 40px 40px 40px;
	max-width: 600px;
}
</style>
